/** Data access for reference data: sites/locations, parties, asset types/models, attribute definitions, inventory setup. */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, type Schemas } from "@/api/client";
import { unwrap } from "@/api/problem";
import { collectPages, compactParams, type Paged, usePagedQuery } from "@/shared/lib/paging";

export type Site = Schemas["SiteOut"];
export type CostCenter = Schemas["CostCenterOut"];
export type CostCenterIn = Schemas["CostCenterIn"];
export type CostCenterPatch = Schemas["CostCenterPatch"];

/** Tenant financial allocation codes, optionally limited to an operating unit. */
export function useCostCenters(operatingUnitId?: string): Paged<CostCenter> {
  return usePagedQuery(["cost-centers", operatingUnitId], async (cursor) =>
    unwrap(
      await api.GET("/api/v1/parties/cost-centers", {
        params: {
          query: compactParams({ operating_unit_id: operatingUnitId, cursor, limit: 100 }),
        },
      }),
    ),
  );
}

/** Load every authorized cost center before complete-list search and sorting. */
export function useCompleteCostCenters(operatingUnitId?: string): UseQueryResult<CostCenter[]> {
  return useQuery({
    queryKey: ["cost-centers", "complete", operatingUnitId],
    queryFn: () =>
      collectPages(async (cursor) =>
        unwrap(
          await api.GET("/api/v1/parties/cost-centers", {
            params: {
              query: compactParams({ operating_unit_id: operatingUnitId, cursor, limit: 100 }),
            },
          }),
        ),
      ),
  });
}

/** Create or rename/deactivate a cost center without rewriting historical allocation. */
export function useSaveCostCenter(): UseMutationResult<
  CostCenter,
  Error,
  { id?: string; body: CostCenterIn | CostCenterPatch }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) =>
      id
        ? unwrap(
            await api.PATCH("/api/v1/parties/cost-centers/{center_id}", {
              params: { path: { center_id: id } },
              body,
            }),
          )
        : unwrap(await api.POST("/api/v1/parties/cost-centers", { body: body as CostCenterIn })),
    onSuccess: () => invalidate(qc, "cost-centers", "picker"),
  });
}
export type SiteIn = Schemas["SiteIn"];
export type SitePatch = Schemas["SitePatch"];
export type Location = Schemas["LocationOut"];
export type LocationIn = Schemas["LocationIn"];
export type LocationPatch = Schemas["LocationPatch"];
export type LocationType = Schemas["LocationTypeOut"];
export type Party = Schemas["PartyOut"];
export type PartyIn = Schemas["PartyIn"];
export type PartyPatch = Schemas["PartyPatch"];
export type ContactIn = Schemas["ContactIn"];
export type AssetType = Schemas["AssetTypeOut"];
export type AssetTypeIn = Schemas["AssetTypeIn"];
export type AssetTypePatch = Schemas["AssetTypePatch"];
export type Model = Schemas["ModelOut"];
export type ModelIn = Schemas["ModelIn"];
export type ModelPatch = Schemas["ModelPatch"];
export type MeterType = Schemas["MeterTypeOut"];
export type MeterTypeIn = Schemas["MeterTypeIn"];
export type LocationTypeIn = Schemas["LocationTypeIn"];
export type HierarchyCompatibility = Schemas["HierarchyCompatibilityOut"];
export type AttributeDefinition = Schemas["AttributeDefinitionOut"];
export type AttributeDefinitionIn = Schemas["AttributeDefinitionIn"];
export type AttributeDefinitionPatch = Schemas["AttributeDefinitionPatch"];
export type TenantSettings = Schemas["TenantSettingsOut"];
export type TenantSettingsPatch = Schemas["TenantSettingsPatch"];
export type NumberSequence = Schemas["NumberSequenceOut"];
export type NumberSequencePatch = Schemas["NumberSequencePatch"];
export type Item = Schemas["ItemOut"];
export type ItemIn = Schemas["ItemIn"];
export type ItemPatch = Schemas["ItemPatch"];
export type ItemCategory = Schemas["CategoryOut"];
export type ItemCategoryIn = Schemas["CategoryIn"];
export type ItemCategoryPatch = Schemas["CategoryPatch"];
export type ReorderPolicy = Schemas["ReorderPolicyOut"];
export type ReorderPolicyIn = Schemas["ReorderPolicyIn"];

/** Enumerations mirrored from the API. */
export const SITE_KINDS = [
  "yard",
  "marina",
  "base",
  "warehouse",
  "customer_site",
  "other",
] as const;
export const PARTY_KINDS = ["organisation", "person"] as const;
export const PARTY_ROLES = [
  "vendor",
  "customer",
  "manufacturer",
  "insurer",
  "class_society",
  "operator",
  "surveyor",
] as const;
export const ASSET_CATEGORIES = [
  "vessel",
  "propulsion",
  "electrical",
  "navigation",
  "safety",
  "deck",
  "hvac",
  "facility",
  "tool",
  "other",
] as const;
export const ITEM_KINDS = ["part", "consumable", "tool", "lubricant", "ppe", "other"] as const;
export const COSTING_METHODS = ["average", "standard"] as const;

function invalidate(qc: ReturnType<typeof useQueryClient>, ...keys: string[]): void {
  for (const key of keys) {
    void qc.invalidateQueries({ queryKey: [key] });
  }
}

// ---------------------------------------------------------------- sites & locations
export function useSites(): UseQueryResult<Site[]> {
  return useQuery({
    queryKey: ["sites"],
    queryFn: async () => unwrap(await api.GET("/api/v1/locations/sites")),
  });
}

export function useSaveSite(): UseMutationResult<
  Site,
  Error,
  { id?: string; body: SiteIn | SitePatch }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) =>
      id
        ? unwrap(
            await api.PATCH("/api/v1/locations/sites/{site_id}", {
              params: { path: { site_id: id } },
              body: body,
            }),
          )
        : unwrap(await api.POST("/api/v1/locations/sites", { body: body as SiteIn })),
    onSuccess: () => invalidate(qc, "sites", "picker"),
  });
}

export function useLocationTypes(): UseQueryResult<LocationType[]> {
  return useQuery({
    queryKey: ["location-types"],
    queryFn: async () => unwrap(await api.GET("/api/v1/locations/location-types")),
    staleTime: 5 * 60_000,
  });
}

export function useCreateLocationType(): UseMutationResult<LocationType, Error, LocationTypeIn> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) =>
      unwrap(await api.POST("/api/v1/locations/location-types", { body })),
    onSuccess: () => invalidate(qc, "location-types"),
  });
}

export function useLocationsOf(siteId: string | undefined): UseQueryResult<Location[]> {
  return useQuery({
    queryKey: ["locations", siteId ?? ""],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/locations/locations", { params: { query: { site_id: siteId } } }),
      ),
    enabled: !!siteId,
  });
}

export function useSaveLocation(): UseMutationResult<
  Location,
  Error,
  { id?: string; body: LocationIn | LocationPatch }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) =>
      id
        ? unwrap(
            await api.PATCH("/api/v1/locations/locations/{location_id}", {
              params: { path: { location_id: id } },
              body: body,
            }),
          )
        : unwrap(await api.POST("/api/v1/locations/locations", { body: body as LocationIn })),
    onSuccess: () => invalidate(qc, "locations", "sites", "picker"),
  });
}

export function useDeleteLocation(): UseMutationResult<unknown, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) =>
      unwrap(
        await api.DELETE("/api/v1/locations/locations/{location_id}", {
          params: { path: { location_id: id } },
        }),
      ),
    onSuccess: () => invalidate(qc, "locations", "sites", "picker"),
  });
}

// ---------------------------------------------------------------- parties
export function useParties(filters: { q?: string; role?: string; active?: boolean }): Paged<Party> {
  return usePagedQuery(["parties", filters], async (cursor) =>
    unwrap(
      await api.GET("/api/v1/parties/", {
        params: { query: compactParams({ ...filters, cursor, limit: 50 }) },
      }),
    ),
  );
}

export function useSaveParty(): UseMutationResult<
  Party,
  Error,
  { id?: string; body: PartyIn | PartyPatch; contacts?: ContactIn[] }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body, contacts }) => {
      const party = id
        ? unwrap(
            await api.PATCH("/api/v1/parties/{party_id}", {
              params: { path: { party_id: id } },
              body: body,
            }),
          )
        : unwrap(await api.POST("/api/v1/parties/", { body: body as PartyIn }));
      if (contacts) {
        await api.PUT("/api/v1/parties/{party_id}/contacts", {
          params: { path: { party_id: party.id } },
          body: contacts,
        });
        return unwrap(
          await api.GET("/api/v1/parties/{party_id}", { params: { path: { party_id: party.id } } }),
        );
      }
      return party;
    },
    onSuccess: () => invalidate(qc, "parties", "picker"),
  });
}

// ---------------------------------------------------------------- asset types & models
export function useAssetTypesAdmin(): UseQueryResult<AssetType[]> {
  return useQuery({
    queryKey: ["asset-types"],
    queryFn: async () => unwrap(await api.GET("/api/v1/assets/types")),
  });
}

export function useSaveAssetType(): UseMutationResult<
  AssetType,
  Error,
  { id?: string; body: AssetTypeIn | AssetTypePatch }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) =>
      id
        ? unwrap(
            await api.PATCH("/api/v1/assets/types/{type_id}", {
              params: { path: { type_id: id } },
              body: body,
            }),
          )
        : unwrap(await api.POST("/api/v1/assets/types", { body: body as AssetTypeIn })),
    onSuccess: () => invalidate(qc, "asset-types", "picker"),
  });
}

export function useModels(assetTypeId?: string): UseQueryResult<Model[]> {
  return useQuery({
    queryKey: ["models", assetTypeId ?? ""],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/assets/models", {
          params: { query: { asset_type_id: assetTypeId } },
        }),
      ),
  });
}

export function useSaveModel(): UseMutationResult<
  Model,
  Error,
  { id?: string; body: ModelIn | ModelPatch }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) =>
      id
        ? unwrap(
            await api.PATCH("/api/v1/assets/models/{model_id}", {
              params: { path: { model_id: id } },
              body: body,
            }),
          )
        : unwrap(await api.POST("/api/v1/assets/models", { body: body as ModelIn })),
    onSuccess: () => invalidate(qc, "models", "picker"),
  });
}

export function useMeterTypes(): UseQueryResult<MeterType[]> {
  return useQuery({
    queryKey: ["meter-types"],
    queryFn: async () => unwrap(await api.GET("/api/v1/meters/types")),
    staleTime: 5 * 60_000,
  });
}

export function useCreateMeterType(): UseMutationResult<MeterType, Error, MeterTypeIn> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => unwrap(await api.POST("/api/v1/meters/types", { body })),
    onSuccess: () => invalidate(qc, "meter-types"),
  });
}

export function useHierarchyCompatibility(): UseQueryResult<HierarchyCompatibility> {
  return useQuery({
    queryKey: ["asset-types", "compatibility"],
    queryFn: async () => unwrap(await api.GET("/api/v1/assets/types/compatibility-check")),
  });
}

// ---------------------------------------------------------------- attribute definitions
export function useAllAttributeDefinitions(
  entityType?: string,
): UseQueryResult<AttributeDefinition[]> {
  return useQuery({
    queryKey: ["attribute-definitions", "admin", entityType ?? ""],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/settings/attribute-definitions", {
          params: { query: { entity_type: entityType, include_inactive: true } },
        }),
      ),
  });
}

export function useEntityTypes(): UseQueryResult<Schemas["EntityTypeOut"][]> {
  return useQuery({
    queryKey: ["attribute-entity-types"],
    queryFn: async () =>
      unwrap(await api.GET("/api/v1/settings/attribute-definitions/entity-types")),
    staleTime: Infinity,
  });
}

export function useDataTypes(): UseQueryResult<{ value: string; label: string }[]> {
  return useQuery({
    queryKey: ["attribute-data-types"],
    queryFn: async () =>
      unwrap(await api.GET("/api/v1/settings/attribute-definitions/data-types")) as {
        value: string;
        label: string;
      }[],
    staleTime: Infinity,
  });
}

export function useSaveAttributeDefinition(): UseMutationResult<
  AttributeDefinition,
  Error,
  { id?: string; body: AttributeDefinitionIn | AttributeDefinitionPatch }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) =>
      id
        ? unwrap(
            await api.PATCH("/api/v1/settings/attribute-definitions/{definition_id}", {
              params: { path: { definition_id: id } },
              body: body,
            }),
          )
        : unwrap(
            await api.POST("/api/v1/settings/attribute-definitions", {
              body: body as AttributeDefinitionIn,
            }),
          ),
    onSuccess: () => invalidate(qc, "attribute-definitions", "asset-types"),
  });
}

export function useDeleteAttributeDefinition(): UseMutationResult<unknown, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) =>
      unwrap(
        await api.DELETE("/api/v1/settings/attribute-definitions/{definition_id}", {
          params: { path: { definition_id: id } },
        }),
      ),
    onSuccess: () => invalidate(qc, "attribute-definitions", "asset-types"),
  });
}

// ---------------------------------------------------------------- tenant general settings & numbering
export function useTenantSettings(enabled = true): UseQueryResult<TenantSettings> {
  return useQuery({
    queryKey: ["tenant-settings"],
    queryFn: async () => unwrap(await api.GET("/api/v1/settings/tenant")),
    enabled,
  });
}

export function useUpdateTenantSettings(): UseMutationResult<
  TenantSettings,
  Error,
  TenantSettingsPatch
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => unwrap(await api.PATCH("/api/v1/settings/tenant", { body })),
    onSuccess: () => invalidate(qc, "tenant-settings", "me"),
  });
}

export function useNumberSequences(enabled = true): UseQueryResult<NumberSequence[]> {
  return useQuery({
    queryKey: ["number-sequences"],
    queryFn: async () => unwrap(await api.GET("/api/v1/settings/number-sequences")),
    enabled,
  });
}

export function useUpdateNumberSequence(): UseMutationResult<
  NumberSequence,
  Error,
  { key: string; body: NumberSequencePatch }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ key, body }) =>
      unwrap(
        await api.PATCH("/api/v1/settings/number-sequences/{key}", {
          params: { path: { key } },
          body,
        }),
      ),
    onSuccess: () => invalidate(qc, "number-sequences"),
  });
}

// ---------------------------------------------------------------- inventory setup
export function useItemCategories(): UseQueryResult<ItemCategory[]> {
  return useQuery({
    queryKey: ["item-categories"],
    queryFn: async () => unwrap(await api.GET("/api/v1/inventory/categories")),
  });
}

export function useCreateItemCategory(): UseMutationResult<
  ItemCategory,
  Error,
  Schemas["CategoryIn"]
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => unwrap(await api.POST("/api/v1/inventory/categories", { body })),
    onSuccess: () => invalidate(qc, "item-categories"),
  });
}

export function useSaveItemCategory(): UseMutationResult<
  ItemCategory,
  Error,
  { id?: string; body: ItemCategoryIn | ItemCategoryPatch }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) =>
      id
        ? unwrap(
            await api.PATCH("/api/v1/inventory/categories/{category_id}", {
              params: { path: { category_id: id } },
              body,
            }),
          )
        : unwrap(await api.POST("/api/v1/inventory/categories", { body: body as ItemCategoryIn })),
    onSuccess: () => invalidate(qc, "item-categories"),
  });
}

export function useDeleteItemCategory(): UseMutationResult<unknown, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) =>
      unwrap(
        await api.DELETE("/api/v1/inventory/categories/{category_id}", {
          params: { path: { category_id: id } },
        }),
      ),
    onSuccess: () => invalidate(qc, "item-categories"),
  });
}

export function useAllReorderPolicies(enabled = true): UseQueryResult<ReorderPolicy[]> {
  return useQuery({
    queryKey: ["reorder-policies", "all"],
    queryFn: async () => unwrap(await api.GET("/api/v1/inventory/reorder/policies")),
    enabled,
  });
}

export function useSaveItem(): UseMutationResult<
  Item,
  Error,
  { id?: string; body: ItemIn | ItemPatch }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) =>
      id
        ? unwrap(
            await api.PATCH("/api/v1/inventory/items/{item_id}", {
              params: { path: { item_id: id } },
              body: body,
            }),
          )
        : unwrap(await api.POST("/api/v1/inventory/items", { body: body as ItemIn })),
    onSuccess: () => invalidate(qc, "items", "picker"),
  });
}

export function useReorderPolicies(itemId: string): UseQueryResult<ReorderPolicy[]> {
  return useQuery({
    queryKey: ["reorder-policies", itemId],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/inventory/reorder/policies", {
          params: { query: { item_id: itemId } },
        }),
      ),
  });
}

export function useSetReorderPolicy(): UseMutationResult<ReorderPolicy, Error, ReorderPolicyIn> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) =>
      unwrap(await api.PUT("/api/v1/inventory/reorder/policies", { body })),
    onSuccess: () => invalidate(qc, "reorder-policies", "reorder-suggestions", "items"),
  });
}
